
import { ListDefaultContentsMinimizedEnum } from '@rtl/api'
import { mixins, Options, Prop, Watch } from 'vue-property-decorator'
import { Action } from 'vuex-class'
import { ContentListPayload, UEFA_ACTION, UEFA_NAMESPACE, UEFA_SEASON_ID, UefaScoreListPayload, UefaScoreListResult } from '../../store'

import { uefaSeries } from '../components/uefa/config'
import UefaContentList from '../components/uefa/UefaContentList.vue'
import PageLayout from '../layouts/PageLayout.vue'
import { FetchContentListMixin } from '../mixins'
import WidgetMixin, { WidgetMeta, widgetMetaFields } from './WidgetMixin'

export const meta: WidgetMeta = {
  label: 'UEFA - Összefoglalók',
  fields: {
    ...widgetMetaFields,
    season: {
      type: 'select',
      default: Object.keys(UEFA_SEASON_ID)[0],
      props: {
        title: 'Szezon',
        items: Object.keys(UEFA_SEASON_ID)
      }
    },
    length: {
      type: Number,
      default: 4,
      props: {
        placeholder: 'Darabszám',
        min: 2
      }
    },
    showSlider: {
      type: Boolean,
      default: false,
      props: {
        label: 'Lapozó'
      }
    },
    fullpageMode: {
      type: Boolean,
      default: false,
      props: {
        label: 'Teljes oldalas'
      }
    },
    fallbackUrl: {
      type: String,
      default: '',
      props: {
        placeholder: 'Fallback url'
      }
    }
  }
}

@Options({
  name: 'UefaContentListWidget',
  components: {
    UefaContentList,
    PageLayout
  }
})
export default class UefaContentListWidget extends mixins(FetchContentListMixin, WidgetMixin) {
  @Action(UEFA_ACTION.SCORE_LIST, { namespace: UEFA_NAMESPACE })
  storeFetchUefaScoreList!: (payload: UefaScoreListPayload) => Promise<UefaScoreListResult>

  @Prop({
    type: String,
    default: Object.keys(UEFA_SEASON_ID)[0]
  })
  readonly season!: keyof typeof UEFA_SEASON_ID

  @Prop({
    type: Number,
    default: 4
  })
  readonly length!: number

  @Prop({
    type: Boolean,
    default: false
  })
  readonly showSlider!: boolean

  @Prop({
    type: Boolean,
    default: false
  })
  readonly fullpageMode!: boolean

  @Prop({
    type: String
  })
  readonly fallbackUrl!: string

  get contentListPayload (): ContentListPayload | null {
    return {
      tags: ['bajnokok-ligaja-eredmenyek'],
      minimized: ListDefaultContentsMinimizedEnum.NUMBER_1,
      limit: 1000
    }
  }

  get filterOptions () {
    return uefaSeries[this.season]
  }

  get selectedFilter () {
    return this.filterOptions.find((filter) => this.$route.query['szurok'] === filter.value) ?? null
  }

  isFilterSelected (value: string) {
    return this.selectedFilter?.value === value
  }

  toggleFilter (value: string) {
    this.$router.push({ query: !this.isFilterSelected(value) ? { szurok: value } : null })
  }

  get scoreList () {
    const data = this.$store.getters['$cache/result']([UEFA_NAMESPACE, UEFA_ACTION.SCORE_LIST], this.uefaScoreListPayload)?.data

    if (data) {
      const getDate = (date: string, time: string) => {
        return new Date(`${date.endsWith('Z') ? date.slice(0, -1) : date} ${time}`)
      }
      return data.match
        .map((match: any) => {
          const homeTeam = match.matchInfo.contestant.find((contestant: any) => contestant.position === 'home')?.shortName
          const awayTeam = match.matchInfo.contestant.find((contestant: any) => contestant.position === 'away')?.shortName
          const matchDate = getDate(match.matchInfo.date, match.matchInfo.time)
          const series = this.filterOptions.findLast((series) => series.tags.findLast((tag) => tag.date < matchDate)) ?? null

          return {
            series: series?.label ?? '',
            date: matchDate,
            items: match.matchInfo.contestant.map((contestant: any) => {
              return {
                name: contestant.name,
                logo: `${this.$store.getters.getEnv('RTL_URL') ?? ''}/uefa/${this.season}/teams/${contestant.shortName.replace('/', '-')}.png`,
                result: match.liveData.matchDetails.scores.total[contestant.position] ?? ''
              }
            }),
            content: this.contentList?.items?.find(content => {
              return content.tags?.some((tag) => !series || series.tags.map((seriesTag) => seriesTag.tag).includes(tag.slug ?? '')) && content.tags?.some((tag, i, tags) => tag.tag === homeTeam && tags[i + 1]?.tag === awayTeam)
            }) ?? {
              leadImageUrls: {
                leadImageUrl: 'https://cdn.rtl.hu/40/6b/ferencvaros-bajnokok-ligaja-playoff_image_2f96b411321239576d48c5c300f4_16-9'
              },
              url: this.fallbackUrl
            }
          }
        })
    }
  
    return []
  }

  get scoreListBySeries () {
    const items = this.scoreList

    return this.filterOptions.map((series) => {
      return {
        ...series,
        items: items.filter((score: any) => score.series === series.label)
      }
    })
  }

  get uefaScoreListPayload (): UefaScoreListPayload {
    return {
      season: UEFA_SEASON_ID[this.season],
      _pgNm: 1,
      _pgSz: this.length
    }
  }

  @Watch('uefaScoreListPayload', { deep: true })
  async fetchUefaScoreList () {
    if (this.uefaScoreListPayload) {
      return this.storeFetchUefaScoreList(this.uefaScoreListPayload)
    }
  }

  serverPrefetch () {
    return this.fetchUefaScoreList()
  }

  mounted () {
    return this.fetchUefaScoreList()
  }
}
